// https://chatgpt.com/c/6917f282-acb8-8328-ba9b-0542a619be32
"use client";

import { useLayoutEffect, useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { useAuth } from "@/context/auth-context";

type Props = {
  categorySlug?: string;
  collectionHierarchy: any[];
};

const HOME_IMAGE_URL = "https://caliiorderissues.s3.us-east-2.amazonaws.com/Inicio.jpg"

export default function CategoriesScrollbar({ categorySlug, collectionHierarchy }: Props) {
  const { user } = useAuth();
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const viewportRef = useRef<HTMLDivElement | null>(null);

  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  // --- Hook up to the actual ScrollArea viewport (Radix-generated) ---
  useLayoutEffect(() => {
    if (!scrollAreaRef.current) return;

    const viewport = scrollAreaRef.current.querySelector(
      "[data-radix-scroll-area-viewport]"
    ) as HTMLDivElement | null;

    if (!viewport) return;

    viewportRef.current = viewport;

    const update = () => {
      const el = viewportRef.current;
      if (!el) return;
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanLeft(scrollLeft > 4);
      setCanRight(scrollLeft + clientWidth < scrollWidth - 4);
    };

    update();
    viewport.addEventListener("scroll", update);
    window.addEventListener("resize", update);

    return () => {
      viewport.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  // --- Scroll by chunk when arrows are clicked ---
  const scroll = (dir: "left" | "right") => {
    const el = viewportRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.7;
    el.scrollBy({
      left: dir === "left" ? -amount : amount,
      behavior: "smooth",
    });
  };

  // --- Auto-scroll to active category when categorySlug changes ---
  useEffect(() => {
    if (!categorySlug || !viewportRef.current) return;

    const el = viewportRef.current;
    const pill = el.querySelector<HTMLButtonElement>(
      `[data-category-id="${categorySlug}"]`
    );
    if (!pill) return;

    pill.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [categorySlug]);

  return (
    <div className="relative w-full border-b bg-background/100 backdrop-blur">
      {/* Fade overlays */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-background to-transparent" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-background to-transparent" />

      {/* Arrows */}
      <button
        type="button"
        onClick={() => scroll("left")}
        className={`absolute left-1 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow transition-opacity duration-200 hover:bg-accent ${
          canLeft ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronLeft className="h-6 w-6" />
      </button>

      <button
        type="button"
        onClick={() => scroll("right")}
        className={`absolute right-1 top-1/2 z-20 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-background shadow transition-opacity duration-200 hover:bg-accent ${
          canRight ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <ChevronRight className="h-6 w-6" />
      </button>

      {/* ScrollArea: add a class hook for scroll-snap styling */}
      <ScrollArea
        ref={scrollAreaRef}
        className="w-full whitespace-nowrap"
      >
        <div className="flex w-max gap-3 px-3 md:px-6 py-2">
          {[{id: '0', name: 'Inicio', preview_image_url: HOME_IMAGE_URL}].concat(collectionHierarchy).map((cat) => {
            const active = cat.id == categorySlug;
            if (!cat.preview_image_url) {
              return null;
            }
            if (parseInt(cat.id) === -1 && !user) {
              return null;
            }
            return (
              <Link href={`/categorias/${cat.id}?prev=${categorySlug}`} key={cat.id}>
                <button
                  type="button"
                  data-category-id={cat.id}
                  className={[
                    "flex cursor-pointer snap-center flex-col items-center shrink-0 w-[70px] md:w-[75px] lg:w-[80px] xl:w-[80px] gap-1 px-2 py-2 rounded-2xl text-[11px] font-medium text-center leading-tight",
                    "transition-transform transition-colors duration-150 ease-out",
                    "hover:scale-[1.02] active:scale-[0.98]",
                    active
                      ? "bg-primary/10 text-primary"
                      : "bg-background text-muted-foreground hover:bg-accent",
                  ].join(" ")}
                >
                  <div
                    className={[
                      "relative mb-1 w-full aspect-square overflow-hidden rounded-xl",
                      "border border-border/60 bg-muted/60 shadow-sm",
                      "transition-transform duration-150",
                      active ? "scale-105" : "",
                    ].join(" ")}
                  >
                    <Image
                      src={cat.preview_image_url}
                      alt={cat.name}
                      fill
                      className="object-contain"
                    />
                  </div>
                  <span className="w-full text-center leading-tight text-[11px] line-clamp-2 truncate text-gray-800 font-semibold">{cat.name}</span>
                </button>
              </Link>
            );
          })}
        </div>

        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
